import { every, noop, get, isEmpty, sortBy } from 'underscore';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { ValidationGroup, withCustomValidation } from '@bingads-webui-react/validation';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { ValidatedAudienceInterests } from '@bingads-webui-campaign-react/audience-interests';

import { withBidAdjustment } from './with-bid-adjustment';

const ValidatedAudienceInterestsWithAudienceInfo = (props) => {
  const {
    shouldHide, onAudienceTargetInfoChange, updateValidation, validationProps, ...rest
  } = props;
  const { data: { interests } } = validationProps;

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: 'AdGroupCriterion',
      isForVNext: true,
      initAudienceAssociationView: false,
    })), []);

  const onChange = () => {
    const updatedItems = currentLevelTargetInfo.getSnapshot(false);

    updateValidation([], { interests: [...updatedItems] });
  };

  const onValidate = (result) => {
    const { data } = result;
    if (!isEmpty(data)) {
      updateValidation([], { interests: data.interests });
    }
  };

  useEffect(() => {
    if (onAudienceTargetInfoChange) {
      onAudienceTargetInfoChange(currentLevelTargetInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevelTargetInfo]);

  useEffect(() => {
    if (currentLevelTargetInfo && interests) {
      currentLevelTargetInfo.off('AdGroupCriterionAudienceChanged', onChange);
      const currentTargets = currentLevelTargetInfo.getSnapshot(false);

      const isSame = every(interests, interest => currentTargets.find(({ Criterion }) => (Criterion.AudienceId === get(interest, 'Criterion.AudienceId'))));

      if ((interests.length !== currentTargets.length) || !isSame) {
        currentLevelTargetInfo.destroyTargetTypeAsync('Audience').then(() => {
          const sortedAudiences = sortBy(interests, 'Status');
          sortedAudiences.forEach((audience) => {
            const { Criterion = {} } = audience;
            const newCriterion = {
              ...audience,
              Criterion: {
                ...Criterion,
              },
            };

            currentLevelTargetInfo.createAsync(newCriterion);
          });
        }).then(() => {
          currentLevelTargetInfo.on('AdGroupCriterionAudienceChanged', onChange);
        });
      } else {
        currentLevelTargetInfo.on('AdGroupCriterionAudienceChanged', onChange);
      }
    }

    return () => {
      currentLevelTargetInfo.off('AdGroupCriterionAudienceChanged', onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interests]);

  if (shouldHide) {
    return null;
  }

  return (
    <ValidationGroup
      validationProps={{
        data: interests,
        onValidate,
        stopPropagation: true,
      }}
    >
      <ValidatedAudienceInterests {...rest} validationProps={{ data: { interests } }} />
    </ValidationGroup>
  );
};

ValidatedAudienceInterestsWithAudienceInfo.propTypes = {
  ...ValidatedAudienceInterests.propTypes,
  shouldHide: PropTypes.bool,
  onAudienceTargetInfoChange: PropTypes.func,
  isAudienceCreationWorkflow: PropTypes.bool,
};

ValidatedAudienceInterestsWithAudienceInfo.defaultProps = {
  shouldHide: false,
  onAudienceTargetInfoChange: noop,
  isAudienceCreationWorkflow: false,
};

export const ValidatedAudienceInterestsWithBidAdjustment = withBidAdjustment(withCustomValidation(ValidatedAudienceInterestsWithAudienceInfo), 'Audience', ['interests']);
